import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { Interaction, InteractionStatus, InteractionType } from "../api/types";
import { humanizeError, humanizePayload } from "../lib/errorMessages";

interface Props {
  open: boolean;
  igAccountId: string;
  type: InteractionType;
  /** Display label for the drawer header. */
  title: string;
  /** Restrict listed rows to those created on/after this ISO timestamp. */
  since?: string;
  onClose: () => void;
}

const STATUS_STYLES: Record<InteractionStatus, string> = {
  success: "bg-emerald-500/15 text-emerald-300",
  failed: "bg-rose-500/15 text-rose-300",
  skipped: "bg-slate-500/20 text-slate-300",
};

const PAGE_SIZE = 25;

/**
 * Slide-in drawer that lists every Interaction for a given (account, type, time window).
 * Used by the dashboard tiles - clicking a tile opens it scoped to that metric. Reuses
 * the existing /api/interactions endpoint so the data matches the activity log exactly.
 */
export default function InteractionDetailDrawer({
  open,
  igAccountId,
  type,
  title,
  since,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<InteractionStatus | "">("");

  // Reset paging whenever the drawer is reopened on a different metric.
  useEffect(() => {
    if (open) {
      setPage(1);
      setStatusFilter("");
    }
  }, [open, type, igAccountId, since]);

  const query = useQuery({
    queryKey: ["interaction-detail", igAccountId, type, since, page, statusFilter],
    queryFn: async () => {
      const { data } = await api.get<{
        items: Interaction[];
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>("/api/interactions", {
        params: {
          igAccountId,
          type,
          status: statusFilter || undefined,
          from: since,
          page,
          pageSize: PAGE_SIZE,
        },
      });
      return data;
    },
    enabled: open && !!igAccountId,
  });

  // Esc closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const items = query.data?.items || [];
  const pagination = query.data?.pagination;

  return (
    <div className="fixed inset-0 z-40">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col border-l border-slate-800 bg-slate-950 shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-xs text-slate-400">
              {pagination
                ? t("dashboard.detail.summary", { total: pagination.total })
                : t("dashboard.detail.loading")}
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary !px-3 !py-1.5"
            onClick={onClose}
            aria-label={t("dashboard.detail.close")}
          >
            ×
          </button>
        </header>

        <div className="border-b border-slate-800 px-5 py-3">
          <label className="label">{t("activity.filters.status")}</label>
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as InteractionStatus | "");
              setPage(1);
            }}
          >
            <option value="">{t("activity.filters.allStatuses")}</option>
            <option value="success">{t("interaction.status.success")}</option>
            <option value="failed">{t("interaction.status.failed")}</option>
            <option value="skipped">{t("interaction.status.skipped")}</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {query.isLoading ? (
            <p className="text-sm text-slate-400">{t("dashboard.detail.loading")}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400">{t("dashboard.detail.empty")}</p>
          ) : (
            <ul className="divide-y divide-slate-800">
              {items.map((row) => {
                const friendlyError = humanizeError(row.error, t);
                const friendlyPayload = humanizePayload(row.payload, t);
                return (
                  <li key={row._id} className="py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          @{row.targetUsername || t("common.dash")}
                        </span>
                        <span className={clsx("pill", STATUS_STYLES[row.status])}>
                          {t(`interaction.status.${row.status}`)}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-400">
                        {format(new Date(row.createdAt), "MMM d, HH:mm:ss")}
                      </p>
                      {friendlyPayload && (
                        <p className="mt-1 line-clamp-2 break-words text-xs text-slate-300">
                          {friendlyPayload}
                        </p>
                      )}
                      {row.error && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-xs text-rose-300">
                            {friendlyError ?? row.error}
                          </summary>
                          {friendlyError && (
                            <pre className="mt-1 whitespace-pre-wrap break-words text-[10px] text-slate-500">
                              {row.error}
                            </pre>
                          )}
                        </details>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {pagination && pagination.totalPages > 1 && (
          <footer className="flex items-center justify-between border-t border-slate-800 px-5 py-3 text-xs text-slate-400">
            <span>
              {t("activity.pagination.summary", {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
              })}
            </span>
            <div className="flex gap-2">
              <button
                className="btn-secondary !px-3 !py-1.5 text-xs"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t("activity.pagination.prev")}
              </button>
              <button
                className="btn-secondary !px-3 !py-1.5 text-xs"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("activity.pagination.next")}
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
